'use strict';

/**
 * Base class for providing security information for the application.
 */
define('vb/types/securityProvider',['ojs/ojconfig',
  'vbsw/private/serviceWorkerManager',
  'vb/private/stateManagement/application',
  'vb/helpers/navigate',
  'urijs/URI',
  'vb/private/configuration',
  'vbsw/private/constants',
  'vb/helpers/bootstrapRest',
  'vb/private/constants',
  'vb/private/utils',
  'vb/private/stateManagement/router',
  'vb/private/types/utils/cryptoUtils',
  'vb/errors/httpError',
], (ojConfig, ServiceWorkerManager, Application, Navigate, URI, Configuration, SwConstants, BootstrapRest,
  Constants, Utils, Router, CryptoUtils, HttpError) => {
  // default timeout for oauth2 code grant flow in seconds
  const OAUTH2_CODE_GRANT_FLOW_TIMEOUT = 237;

  /**
   * Provides functionality for handling security-related tasks such as authentication, authorization,
   * and cookie management.
   * @category Helper
   */
  class SecurityProvider {
    /**
     * Creates a new instance of the SecurityProvider class.
     */
    constructor() {
      globalThis.vbInitConfig = globalThis.vbInitConfig || {};

      // The name of the query parameter used for the returnPath in the loginUrl
      this.returnPathQueryParam = 'endUri';

      // default the userInfo
      this.userInfo = {
        userId: Constants.User.DEFAULT,
        username: Constants.User.DEFAULT,
        longId: Constants.User.DEFAULT,
        fullName: Constants.User.DEFAULT,
        email: Constants.User.DEFAULT,
        roles: /** @type {Array} */ ([]),
        permissions: /** @type {Array} */ ([]),
        isAdmin: false,
        isAuthenticated: false,
      };

      // install this security provider as a message handler to handle messages posted from the
      // service worker and its plugins
      ServiceWorkerManager.getInstance().installMessageHandler(this);
    }

    /**
     * Populate this instance with security information that matches the type returned from {@link getType} using the
     * given config which is the configuration object specified in the userConfig definition in the application
     * descriptor.
     *
     * @param {Object} config - Configuration object from the userConfig definition in the application descriptor.
     * @returns {Promise<void>} A promise that resolves when the initialization is complete.
     */
    initialize(config) {
      this.config = config;

      // set a global property to bypass endpoint proxy which is true by default unless
      // userConfig.configuration.disableTokenRelay is set to true
      // TODO: find a better way to pass this flag or it may not matter once we turn off proxy altogether
      globalThis.vbInitConfig.bypassProxy = config ? !config.disableTokenRelay : true;

      const vbConfig = globalThis.vbInitConfig;
      // get the appPath that will be used for the login return path
      const appPath = vbConfig && vbConfig.APP_PATH;
      if (typeof appPath === 'string') {
        this.appPath = appPath.trim();
      } else {
        // if APP_PATH is not on vbInitConfig, default to the ApplicationUrl
        this.appPath = Configuration.applicationUrl;
      }

      // Always terminate with a '/'
      this.appPath = Utils.addTrailingSlash(this.appPath);

      const { isAuthenticated } = this.userInfo;

      // we need to install the service worker plugins first since the current user request needs to be
      // processed by the offline handler and then the plugins
      return this.getServiceWorkerPlugins(config, !isAuthenticated)
        .then((plugins) => this.installServiceWorkerPlugins(plugins))
        .then(() => this.fetchCurrentUser(config))
        .then(() => {
          // reinstall the plugins if the authenticated status has changed but set reload to false so
          // only those plugins whose metadata has changed are reloaded
          if (isAuthenticated !== this.userInfo.isAuthenticated) {
            return this.getServiceWorkerPlugins(config, isAuthenticated)
              .then((plugins) => this.installServiceWorkerPlugins(plugins, false));
          }
          return Promise.resolve();
        });
    }

    /**
     * Installs the given plugins on all registered service workers. The plugins should be an array of
     * urls. For example:
     *
     *  [
     *   'vbsw/plugin1',
     *   {
     *     url: 'vbsw/plugin2',
     *     params:  {
     *       foo: 'bar'
     *     }
     *   }
     * ]
     *
     * @param {Array<String|{url: String, params: Object}>} [plugins=[]] An array of plugin URLs or objects containing
     * URL and params properties.
     * @param {boolean} [reload=true] Whether to force reload all plugins or only reload those with changed metadata.
     * @returns {Promise<void>} A promise resolving when installation is complete.
     */
    // eslint-disable-next-line class-methods-use-this
    installServiceWorkerPlugins(plugins = [], reload = true) {
      return ServiceWorkerManager.getInstance().installServiceWorkerPlugins(plugins, reload);
    }

    /**
     * Process the array of roles or permissions. From the raw value of the roles
     * or permissions property of the security config payload, calculate an array
     * of roles.
     *
     * If merge is true, the override array will be merged into the original array.
     * Otherwise, the override array, if provided, will replace the original array.
     *
     * @param {Array|String} original an array or a comma separated list of items
     * @param {Array|undefined} override an array
     * @param {Boolean} merge if true, merge original array with override
     * @return {Array} the processed array.
     */
    // eslint-disable-next-line class-methods-use-this
    processRolesOrPerms(original, override, merge = false) {
      // eslint-disable-next-line no-param-reassign
      original = original || [];
      const origArray = Array.isArray(original) ? original : original.split(',');

      let processedArray;
      if (merge) {
        // merge the original array with the override
        processedArray = origArray.concat(override || []);
      } else {
        // replace the original array with the override
        processedArray = override || origArray;
      }

      // Add a property to the array for each item in the array.
      // This will allow expression like $application.user.roles.foo to be truthy
      processedArray.forEach((elt) => {
        processedArray[elt] = true;
      });

      return processedArray;
    }

    /**
     * Retrieves user roles from the runtime environment to override the roles defined in the security configuration.
     *
     * @async
     * @returns {Promise<String[]|undefined>} A promise that resolves to an array of user role strings or undefined.
     */
    // eslint-disable-next-line class-methods-use-this
    getUserRolesOverride() {
      return Application.runtimeEnvironment.getUserRolesOverride();
    }

    /**
     * Retrieves user permissions from the runtime environment to override the permissions defined in the
     * security configuration.
     *
     * @async
     * @returns {Promise<String[]|undefined>} A promise that resolves to an array of strings representing user
     * permissions or undefined if there are no overrides.
     */
    // eslint-disable-next-line class-methods-use-this
    getUserPermissionsOverride() {
      return Application.runtimeEnvironment.getUserPermissionsOverride();
    }

    /**
     * Returns information regarding custom login.
     *
     * @returns {object} Custom login information.
     */
    // eslint-disable-next-line class-methods-use-this
    getCustomLoginInfo() {
      return {};
    }

    /**
     * Fetches the current user information using the provided configuration object.
     *
     * @param {Object} config - The configuration object containing the URL for fetching the current user.
     * @param {String} [config.url] - The URL for fetching the current user.
     * @param {String} [config.endpoint] - The endpoint for fetching the current user.
     * @param {Object} [config.uriParams] - Additional URI parameters for the endpoint.
     *
     * @returns {Promise<Object>} A promise that resolves when the current user information is fetched successfully.
     */
    fetchCurrentUser(config) {
      let responsePromise;

      const vbConfig = globalThis.vbInitConfig;

      if (vbConfig.INJECTED && vbConfig.INJECTED.security && vbConfig.INJECTED.security.user) {
        // return injected user profile
        const result = {
          body: vbConfig.INJECTED.security.user,
          response: { ok: true },
        };
        responsePromise = Promise.resolve(result);
      } else {
        // chose which way to fetch the data, based on 'endpoint'
        responsePromise = config.endpoint
          ? this.fetchCurrentUserFromEndpoint(config.endpoint, config.uriParams)
          : this.fetchCurrentUserRaw(config);
      }

      return responsePromise.then((result) => {
        const { response } = result;

        if (response.ok) {
          // if we use the helper, the json is already resolved (and cannot be re-resolved)
          const jsonPromise = result.body ? Promise.resolve(result.body) : response.json();

          return Promise.all([
            jsonPromise,
            // get the user roles from the runtimeEnvironment if any to override the roles in securityConfig
            this.getUserRolesOverride(),
            // get the user permissions from the runtimeEnvironment if any to override
            // the permissions in securityConfig
            this.getUserPermissionsOverride(),
            this.getCustomLoginInfo(),
          ]).then(([securityConfig, rolesOverride, permissionsOverride, customLoginInfo]) => {
            const roles = this.processRolesOrPerms(securityConfig.roles, rolesOverride);
            const permissions = this.processRolesOrPerms(securityConfig.permissions, permissionsOverride);

            if (securityConfig.isAnonymous !== undefined && !securityConfig.isAnonymous) {
              this.userInfo = {
                userId: securityConfig.id,
                username: securityConfig.userName,
                longId: securityConfig.longId,
                fullName: securityConfig.fullName,
                email: securityConfig.email,
                roles,
                permissions,
                isAdmin: securityConfig.isAdmin,
                isAuthenticated: !securityConfig.isAnonymous,
              };
            }

            this.userInfo.roles = roles;
            this.userInfo.permissions = permissions;

            this.loginUrl = securityConfig.loginUrl;
            this.logoutUrl = securityConfig.logoutUrl;

            this.customLoginInfo = customLoginInfo;
          });
        }

        return Promise.resolve();
      }).catch((err) => {
        // ignore the error
        console.error(err);
      });
    }

    /**
     * Fetches the current user information using the provided configuration object.
     *
     * @param {Object} config - The configuration object containing the URL for fetching the current user.
     * @param {String} [config.url] - The URL for fetching the current user.
     *
     * @returns {Promise<Object>} A promise that resolves with an object containing the response from the fetch call.
     */
    // eslint-disable-next-line class-methods-use-this
    fetchCurrentUserRaw(config) {
      return Promise.resolve()
        .then(() => {
          const options = {
            credentials: 'same-origin',
            headers: {
              [SwConstants.USE_CACHED_RESPONSE_WHEN_OFFLINE]: true, // use offline toolkit to handle caching
              'Cache-Control': 'no-cache, no-store', // bypass browser cache
              Pragma: 'no-cache', // bypass IE browser cache
              [Constants.Headers.VB_INFO_EXTENSION]: '{}', // keep it from being mapped, but skip auth processing
              [Constants.Headers.USE_OAUTH_ACCESS_TOKEN_WHEN_AVAILABLE]: true,
            },
          };

          let { url } = config;

          // if the current user url is a relative url, e.g., resources/userData.json, make sure it's relative
          // to the application url
          if (!Utils.isAbsolutePath(url)) {
            url = `${Configuration.applicationUrl}${url}`;
          }

          // @ts-expect-error otherwise cannot pass boolean headers
          const request = new Request(url, options);

          return fetch(request);
        })
        // make the raw result look like the helper one
        .then((response) => ({
          response,
        }));
    }

    /**
     * Fetches the current user information using the provided endpoint.
     *
     * @param {String} endpoint - The endpoint to use for fetching the current user.
     * @param {Object} [parameters] - Optional parameters to include in the request.
     * @returns {Promise<Object>} A promise that resolves with the response from the endpoint.
     */
    // eslint-disable-next-line class-methods-use-this
    fetchCurrentUserFromEndpoint(endpoint, parameters) {
      return Promise.resolve()
        .then(() => {
          const helper = BootstrapRest.get(endpoint); // no 'container', will use 'base' services only
          if (parameters) {
            helper.parameters(parameters);
          }
          return helper.fetch();
        });
    }

    /**
     * Returns a promise that resolves to an array of plugin information.
     * Each plugin info can be either a URL string or an object containing `url` and `params` properties.
     * The params property will be passed to the constructor of the plugin.
     *
     * Example:
     * ```json
     * [
     *   'vbsw/plugin1',
     *   {
     *     "url": 'vbsw/plugin2',
     *     "params": {
     *       "foo": 'bar'
     *     }
     *   }
     * ]
     *
     * @param {Object} config - User configuration object for service worker plugins.
     * @param {boolean} [isAnonymous=false] - Flag indicating whether the user is anonymous.
     * @returns {Promise<Array>} Promise resolving to an array of plugin information
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    getServiceWorkerPlugins(config, isAnonymous = false) {
      // first get the plugins defined in the config object
      const plugins = (config && config.plugins) ? config.plugins.slice() : [];

      plugins.push({
        url: 'vbsw/private/plugins/generalHeadersHandlerPlugin',
        params: {
          'Accept-Language': ojConfig.getLocale(),
        },
      });

      return Promise.resolve(plugins);
    }

    /**
     * Returns an array of plugin URLs necessary for FA applications to make REST requests in a VB environment.
     *
     * @static
     * @returns {Promise<Array<String|{url: String, params: Object}>>} A promise resolving to an array of plugin
     * URLs or objects containing `url` and `params` properties.
     * @deprecated Since version 2010.0.0-rc.3, to be replaced by
     * `SecurityHelpers.getDefaultServiceWorkerPluginsForVbEnvironment`.
     */
    static getServiceWorkerPluginsForVbEnvironment() {
      return Promise.resolve().then(() => {
        const vbConfig = globalThis.vbInitConfig || {};
        const vbServer = vbConfig.VB_SERVER;
        const serverRoot = Utils.addTrailingSlash(Utils.cleanUpExtraSlashes(vbConfig.INGRESS_PATH || ''));
        const orgId = vbConfig.ORGANIZATION_ID;

        // determine if we are running in a VB environment by checking if the VB_SERVER property
        // is injected into vbInitConfig
        if (vbServer) {
          return [
            // if orgId is not undefined, install multiTenantCsrfTokenHandlerPlugin instead
            (orgId === undefined) ? 'vbsw/private/plugins/csrfTokenHandlerPlugin'
              : {
                url: 'vbsw/private/plugins/multiTenantCsrfTokenHandlerPlugin',
                params: {
                  orgId,
                  vbServer,
                  serverRoot,
                },
              },
          ];
        }

        return [];
      });
    }

    /**
     * Returns an object describing the structure of the user information.
     *
     * @returns {Object} An object containing the types of user information.
     * @property {String} userId - The type of the user ID.
     * @property {String} username - The type of the username.
     * @property {String} fullName - The type of the full name.
     * @property {String} email - The type of the email address.
     * @property {String[]} roles - The type of the roles array.
     * @property {String[]} permissions - The type of the permissions array.
     * @property {boolean} isAuthenticated - The type of the authentication status.
     */
    static getUserInfoType() {
      return {
        userId: 'string',
        username: 'string',
        fullName: 'string',
        email: 'string',
        roles: 'string[]',
        permissions: 'string[]',
        isAuthenticated: 'boolean',
      };
    }

    /**
     * Handles a load error that occurred while loading a page.
     *
     * Attempts to handle the load error for a VB artifact and returns true if successful.
     * Depending on the type of error provided, a security provider can either:
     *    1) handle the error which possibly means redirecting to a login page then return true.
     *    2) do nothing and return false, indicating the error was not handled
     *
     * @param {Error} error - The load error that occurred.
     * @param {String} returnPath - The path of the page that was being loaded when the error occurred.
     *
     * @returns {boolean} True if the error was handled, false otherwise. If the error is handled, the
     * client cancels the current operation; otherwise, the client displays an error message.
     */
    handleLoadError(error, returnPath) {
      if (HttpError.isUnauthorized(error) || (HttpError.isForbidden(error) && !this.userInfo.isAuthenticated)) {
        return this.handleLogin(returnPath);
      }

      return false;
    }

    /**
     * Handles the user lock session process.
     *
     * Custom security providers that need locking support should implement this method.
     * The default behavior is to return false, indicating that no locking is handled.
     *
     * @async
     * @returns {Promise<boolean>} True if the lock is handled, false otherwise.
     */
    // eslint-disable-next-line class-methods-use-this
    async handleLock() {
      // Custom security providers that need locking support should implement this method
      // The default behavior is to return false as no locking is handled
      return false;
    }

    /**
     * Handle the user login process.
     * Redirect to the login page using the login URL given by the security provider configuration.
     * If defined, the returnPath is added to the login URL using the query parameter name defined in the
     * 'returnPathQueryParam' property of the SecurityProvider class.
     *
     * @param {String} [returnPath] - The path of the page to go to when login is successful.
     * @returns {boolean} True if the login process is handled.
     */
    handleLogin(returnPath) {
      if (this.customLoginInfo && this.customLoginInfo.isEnabled) {
        this.handleCustomLogin();
        return true;
      }

      if (this.loginUrl) {
        // Let the router calculate the correct URL from the page path
        let endUri = Navigate.getUrlFromPath(returnPath);
        // Prefix the return path with the appPath
        endUri = `${this.appPath}${endUri}`;

        Navigate.toUrl({
          url: this.loginUrl,
          // Add the return URL as a request parameter
          params: {
            [this.returnPathQueryParam]: endUri,
          },
          // Use replace mode so the login page doesn't appear in the history
          history: Constants.HistoryMode.REPLACE,
        });

        return true;
      }

      return false;
    }

    /**
     * Initiates the custom login flow by navigating to the custom login page,
     * which can be local to the current application or reside in an external application.
     */
    handleCustomLogin() {
      const {
        loginRoot, loginPath, returnPathUrlParamName, loginAppRouterStrategy,
      } = this.customLoginInfo;
      const returnPath = window.location.href;

      if (loginRoot) {
        // If loginRoot is provided, it means the login page is in an external app. Therefore, we need to create
        // the full url from concatenating loginRoot with the result of calling Router.getUrlFromPath on loginPath
        const url = `${loginRoot}${Router.getUrlFromPath(loginPath, loginAppRouterStrategy)}`;
        Navigate.toUrl({
          url,
          params: {
            [returnPathUrlParamName]: returnPath,
          },
          // Use replace mode so the login page doesn't appear in the history
          history: Constants.HistoryMode.REPLACE,
        });
      } else {
        // Otherwise, the custom login page is located in the same app, so we simply navigate to loginPath relative
        // to the application container.
        Router.queueNavigate(Application, {
          page: loginPath,
          params: {
            [returnPathUrlParamName]: returnPath,
          },
          // Use replace mode so the login page doesn't appear in the history
          history: Constants.HistoryMode.REPLACE,
        });
      }
    }

    /**
     * Returns the login URL with the return path query parameter appended.
     *
     * @returns {String|null} The login URL with the return path query parameter,
     * or null if no login URL is configured.
     */
    getLoginUrl() {
      if (this.loginUrl) {
        // Let the router calculate the correct URL from the page path
        let endUri = Navigate.getUrlFromPath('/');
        // Prefix the return path with the appPath
        endUri = `${this.appPath}${endUri}`;

        return `${this.loginUrl}&${this.returnPathQueryParam}=${endUri}`;
      }

      return null;
    }

    /**
     * Handles the user logout process.
     * Navigates to the URL defined by the `logoutUrl` argument. If `logoutUrl` is not provided,
     * it defaults to the `logoutUrl` property of the SecurityProvider configuration.
     *
     * @param {String} [logoutUrl] - The URL to navigate to after logging out. Defaults to the
     *                               `logoutUrl` property of the SecurityProvider configuration.
     * @returns {void}
     */
    handleLogout(logoutUrl) {
      const url = logoutUrl || this.logoutUrl;

      if (url) {
        Navigate.toUrl({
          url,
          // Add the return URL as a request parameter. The return URL is the home page
          params: { [this.returnPathQueryParam]: this.appPath },
          // Use replace mode so the logout page doesn't appear in the history
          history: Constants.HistoryMode.REPLACE,
        });
      }
    }

    /**
     * Checks if the current user has access to a specific resource based on the provided access information.
     * If the user is not authenticated, this method returns false.
     * Otherwise, if the user role is one of the roles in accessInfo or if the user
     * permission is one of the permissions in accessInfo then the method returns true.
     *
     * @param {String} type - The type of resource being accessed (e.g., "Application", "Flow", or "Page").
     * @param {String} path - The path of the resource being accessed (e.g., "app/flow1/myPage").
     * @param {Object} [accessInfo={ requiresAuthentication: true, roles: [], permissions: [] }] - An object describing
     * the access restrictions for the resource.
     * @param {boolean} [accessInfo.requiresAuthentication=true] - Whether authentication is required to access
     * the resource.
     * @param {String[]} [accessInfo.roles=[]] - An array of roles allowed to access the resource.
     * @param {String[]} [accessInfo.permissions=[]] - An array of permissions allowed to access the resource.
     * @returns {boolean} True if the current user satisfies the access restrictions, false otherwise.
     */
    isAccessAllowed(type, path, accessInfo = { requiresAuthentication: true, roles: [], permissions: [] }) {
      // type and path are only needed for potential subclass
      const roles = accessInfo.roles || [];
      const permissions = accessInfo.permissions || [];
      const requiresAuthentication = (accessInfo.requiresAuthentication !== undefined)
        ? accessInfo.requiresAuthentication : true;
      let result = false;

      if (requiresAuthentication) {
        if (!this.userInfo.isAuthenticated) {
          return false;
        }

        // true if at least one of the user roles match the access role or
        // if at least one of the user permissions match the access permissions
        result = roles.some((role) => this.userInfo.roles.includes(role))
          || permissions.some((perm) => this.userInfo.permissions.includes(perm));
      }

      // also return true if there are no required roles or permissions
      return result || (roles.length === 0 && permissions.length === 0);
    }

    // eslint-disable-next-line class-methods-use-this
    securityCallback() {
      return Promise.resolve();
    }

    /**
     * Initiates the OAuth2 code grant flow by redirecting to the given redirectUrl in a new tab and waits
     * for completion event to be posted from the new tab.
     *
     * If the redirect URL or params already include OAuth state, the state is owned by the caller and is preserved.
     * Otherwise VB-RT generates and validates its own state for the popup completion message.
     *
     * @param {String} redirectUrl - URL for the third-party OAuth endpoint.
     * @param {Object} [params={}] - Query parameters for the redirectUrl.
     * @param {Object} [userInteraction={}] - Properties related to the user interaction part of the flow
     * @param {Number} [userInteraction.timeout=OAUTH2_CODE_GRANT_FLOW_TIMEOUT] - Number in seconds to wait for the
     * flow to complete
     * @param {String} [userInteraction.windowFeatures=''] - Comma-separated list of window features in the form.
     * See @link https://developer.mozilla.org/en-US/docs/Web/API/Window/open
     * name=value passed to window.open
     *
     * @returns {Promise<void>} Resolves when the OAuth2 code grant flow is completed,
     * rejects if the flow fails or times out.
     */
    // eslint-disable-next-line class-methods-use-this
    vbRunOAuth2CodeGrantFlow(redirectUrl, params = {}, {
      timeout = OAUTH2_CODE_GRANT_FLOW_TIMEOUT,
      windowFeatures = '',
    } = {}) {
      return new Promise((resolve, reject) => {
        const authUri = new URI(redirectUrl);
        // Some callers, such as TRAP token relay, build the authorize URL and own OAuth state
        // validation. Preserve caller-owned state instead of replacing it with a VB-RT value.
        const hasExternalState = !!(authUri.search(true).state || params.state);
        const state = hasExternalState ? undefined : CryptoUtils.generateCodeVerifier();
        const storageKey = state && `orcl.vbcs.oauth2CodeGrant.${state}`;

        Object.entries(params).forEach(([key, value]) => {
          if (key !== 'state' || !authUri.search(true).state) {
            authUri.addSearch(key, value);
          }
        });

        if (state) {
          authUri.addSearch('state', state);
          sessionStorage.setItem(storageKey, state);
        }

        if (globalThis.open(authUri.href(), '_blank', windowFeatures)) {
          let timer;
          let listener;

          const cleanup = () => {
            if (storageKey) {
              sessionStorage.removeItem(storageKey);
            }
            globalThis.removeEventListener('message', listener);
            clearTimeout(timer);
          };

          // listener for waiting for the code grant flow to complete
          listener = (e) => {
            if (e.origin === globalThis.location.origin && e.data.method === 'vbOAuth2CodeGrantFlowCompleted') {
              const returnedState = e.data.args && e.data.args[0];
              const expectedState = storageKey && sessionStorage.getItem(storageKey);
              cleanup();

              // Only validate state generated by this method. Caller-owned state is validated by
              // the component that owns the OAuth callback and code exchange.
              if (expectedState && (!returnedState || returnedState !== expectedState)) {
                reject(new Error('OAuth state validation failed.'));
                return;
              }

              resolve();
            }
          };

          // can't use { once: true } because non-related messages can come in and remove the listener
          globalThis.addEventListener('message', listener);
          timer = setTimeout(() => {
            cleanup();
            reject(new Error('OAuth2 Code Grant flow timed out.'));
          }, timeout * 1000);
        } else {
          if (storageKey) {
            sessionStorage.removeItem(storageKey);
          }
          reject(new Error('OAuth2 Code Grant flow failed due to inability to open a new browser tab.'));
        }
      });
    }

    /**
     * This method is used to handle the 'vbGetCookie' message from the FetchHandlerPlugin class
     * Retrieves the value of a cookie with the specified name.
     *
     * @param {String} name - The name of the cookie to retrieve.
     * @returns {Promise<*>} A promise that resolves with the decoded cookie value,
     * or null if the cookie does not exist.
     */
    vbGetCookie(name) {
      return Promise.resolve().then(() => {
        const cookie = document.cookie.split(';').find((item) => item.includes(`${name}=`));

        if (cookie) {
          return this.decodeCookie(cookie);
        }

        return null;
      });
    }

    /**
     * This method is used to handle the 'vbDeleteCookie' message from the FetchHandlerPlugin class.
     *
     * @param {String} name - The cookie name to remove and to set on the document.cookie API which is rendered as per
     *    https://wicg.github.io/cookie-store/#set-cookie-algorithm
     * @returns {Promise<void>} A promise that resolves when the cookie has been deleted.
     */
    // eslint-disable-next-line class-methods-use-this
    vbDeleteCookie(name) {
      return Promise.resolve().then(() => {
        const cookieString = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;

        document.cookie = cookieString;
      });
    }

    /**
     * This method is used to handle the 'vbSetCookie' message from the FetchHandlerPlugin class.
     *
     * @param {String} name - The name of the cookie to set.
     * @param {Object} cookie - The value to set on the document.cookie API which is rendered as per
     *    https://wicg.github.io/cookie-store/#set-cookie-algorithm
     * @returns {Promise<void>} A promise that resolves when the cookie has been set.
     */
    vbSetCookie(name, cookie) {
      return Promise.resolve().then(() => {
        const encodedCookie = this.encodeCookie(name, cookie);

        document.cookie = encodedCookie;
      });
    }

    /**
     * Decodes a URL-encoded cookie and parses it into a JSON object.
     *
     * @param {String} cookie - The cookie string to decode.
     * @returns {*} The decoded cookie value as a JSON object, or null if the cookie is empty.
     */
    // eslint-disable-next-line class-methods-use-this
    decodeCookie(cookie) {
      const value = cookie.substring(cookie.indexOf('=') + 1);

      // decode the uri encoded cookie and parse it into a JSON object
      if (value) {
        return JSON.parse(decodeURIComponent(value));
      }

      return null;
    }

    /**
     * JSON stringify the given cookie and url encode it. Also, append additional directives to the cookie based
     * on information provided by the cookie.
     *
     * @param {String} name - The name of the cookie to encode.
     * @param {Object} cookie - The cookie object to encode.
     * @returns {String} The object encoded as a string.
     */
    // eslint-disable-next-line class-methods-use-this
    encodeCookie(name, cookie) {
      let cookieString = `${name}=${encodeURIComponent(JSON.stringify(cookie))}`;
      const append = (...args) => {
        cookieString = cookieString.concat('; ', ...args);
      };

      // append cookie directives based on information provided by the cookie

      let host;
      if (cookie.url) {
        host = new URI(cookie.url).host();
      }

      if (cookie.expires) {
        append('Expires=', new Date(cookie.expires).toUTCString());
      }

      if (cookie.domain) {
        append('Domain=', cookie.domain);
      } else if (host) {
        append('Domain=', host);
      }

      if (cookie.path) {
        append('Path=', cookie.path);
      }

      const sameSite = cookie.sameSite === 'strict' || cookie.sameSite === 'none' ? cookie.sameSite : 'lax';
      const secure = cookie.secure || globalThis.location.protocol === 'https:' || sameSite === 'none';

      if (secure) {
        append('Secure');
      }

      switch (sameSite) {
        case 'strict':
          append('SameSite=', 'Strict');
          break;
        case 'none':
          append('SameSite=', 'None');
          break;
        default:
          append('SameSite=', 'Lax');
          break;
      }

      return cookieString;
    }
  }

  return SecurityProvider;
});

